/**
 * Edit profile modal
 */
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAccount } from 'wagmi';
import { IUser } from '@kattana-io/common';
import Text from '../../../atoms/Text';
import Image from '../../../atoms/Image';
import Button from '../../../molecules/Button';
import { UserAPI } from '../../../store/createStore';
import { actionRefreshCompetition } from '../../../actions/competition';
import { actionCreateNotification } from '../../../actions/notifications';
import {
  validateFacebook,
  validateGithub,
  validateTwitter,
} from './validation';

import './styles.scss';

interface IEditProfileModalProps {
  user: IUser;
  network: 'EVM' | 'ELROND' | 'TRON' | 'TON';
  authToken: string;
  onCancel: () => void;
}

const EditProfileModal = ({
  user,
  network,
  authToken,
  onCancel,
}: IEditProfileModalProps) => {
  const [nickname, setNickname] = useState(user.nickname);
  const [twitter, setTwitter] = useState(user.twitter_url);
  const [facebook, setFacebook] = useState(user.facebook_url);
  const [github, setGithub] = useState(user.github_url);

  const [loading, setLoading] = useState(false);

  const { address } = useAccount();
  const dispatch = useDispatch();

  const handleNicknameChange = (e: any) => {
    const text = e.target.value;

    if (text.length > 23 && text[24]) {
      setNickname(text.slice(0, 23) + text[24]);
    } else {
      setNickname(text);
    }
  };

  const handleFacebookChange = (e: any) => {
    const text = e.target.value;

    return setFacebook(text);
  };

  const handleGithubChange = (e: any) => {
    const text = e.target.value;

    return setGithub(text);
  };

  const handleTwitterChange = (e: any) => {
    const text = e.target.value;

    return setTwitter(text);
  };

  const handleUpdate = async () => {
    if (address) {
      setLoading(true);
      const data = await UserAPI.updateUser(address, network, authToken, {
        nickname: nickname || user.nickname,
        twitter_url: twitter,
        facebook_url: facebook,
        github_url: github,
      });
      setLoading(false);
      if (data?.status === 'User updated') {
        dispatch(actionRefreshCompetition());
      } else {
        dispatch(
          actionCreateNotification({
            kind: 'error',
            message: 'Something went wrong...',
          })
        );
      }
      onCancel();
    }
  };

  const isGithubValid = validateGithub(github);
  const isFacebookValid = validateFacebook(facebook);
  const isTwitterValid = validateTwitter(twitter);

  return (
    <>
      <div className='edit_profile_modal__backdrop' onClick={onCancel} />
      <div className='edit_profile_modal no_scroll'>
        <div className='edit_profile_modal__header'>
          <Text size={16}>Edit profile</Text>
          <div
            className='edit_profile_modal__header__close icon-close'
            onClick={onCancel}
          />
        </div>
        <div className='edit_profile_modal__body'>
          <div className='edit_profile_modal__body__profile'>
            <Image size='avatar' src={user.img_url} />
            <input
              type='text'
              className='field'
              value={nickname || ''}
              onChange={handleNicknameChange}
            />
          </div>
          <div className='edit_profile_modal__body__socials'>
            <div className='edit_profile_modal__body__socials__item'>
              <Text
                size={12}
                className='edit_profile_modal__body__socials__item__label'
              >
                Twitter
              </Text>
              <input
                type='text'
                className={`field ${!isTwitterValid ? 'field--error' : ''}`}
                value={twitter || ''}
                onChange={handleTwitterChange}
              />
            </div>
            <div className='edit_profile_modal__body__socials__item'>
              <Text
                size={12}
                className='edit_profile_modal__body__socials__item__label'
              >
                Facebook
              </Text>
              <input
                type='text'
                className={`field ${!isFacebookValid ? 'field--error' : ''}`}
                value={facebook || ''}
                onChange={handleFacebookChange}
              />
            </div>
            <div className='edit_profile_modal__body__socials__item'>
              <Text
                size={12}
                className='edit_profile_modal__body__socials__item__label'
              >
                Github
              </Text>
              <input
                type='text'
                className={`field ${!isGithubValid ? 'field--error' : ''}`}
                value={github || ''}
                onChange={handleGithubChange}
              />
            </div>
          </div>
        </div>
        <div className='edit_profile_modal__btn'>
          <Button
            loading={loading}
            size='m'
            variant='primary'
            action={handleUpdate}
            disabled={!isFacebookValid || !isGithubValid || !isTwitterValid}
            text='Update'
          />
        </div>
      </div>
    </>
  );
};

export default EditProfileModal;
